import { apiService } from '../../../components/api/apiService';
import type { LucideIcon } from 'lucide-react';
import { lucideIconFromRef } from './lucideIconFromRef';
import {
  SIDEBAR_CATEGORIES,
  SIDEBAR_CATEGORY_ICONS,
  SIDEBAR_CATEGORY_LABELS,
  SIDEBAR_PALETTE_ITEMS,
  SIDEBAR_PALETTE_SECTION_ORDER,
  compareWorkflowBuilderCategoryRows,
  pickAgentPalettePresentation,
  type SidebarCategoryId,
  type SidebarPaletteItem,
} from './Sidebar/sidebarPalette';

const WORKFLOW_BUILDER_CONTROLS_PATH = '/api/WorkflowBuilderControls';

export type WorkflowBuilderCategoryDto = {
  unique_id: string;
  uuid?: string;
  category: string;
  label: string;
  icon: string;
  sort_order: number;
  status?: boolean;
  is_active?: boolean;
};

export type WorkflowBuilderControlDto = {
  unique_id: string;
  uuid?: string;
  category: string;
  icon: string;
  icon_canvas?: string;
  kind: string;
  label: string;
  palette_type: string;
  status?: boolean;
  is_active?: boolean;
  resource_category?: string;
  resourceCategory?: string;
  action_id?: string;
  actionId?: string;
};

export type WorkflowBuilderControlsResponse = {
  source?: string;
  generated_for?: string;
  categories: WorkflowBuilderCategoryDto[];
  controls: WorkflowBuilderControlDto[];
  counts?: { categories?: number; controls?: number; note?: string };
};

function isSidebarCategoryId(value: string): value is SidebarCategoryId {
  return (Object.values(SIDEBAR_CATEGORIES) as string[]).includes(value);
}

function dtoCategoryActive(dto: WorkflowBuilderCategoryDto): boolean {
  return dto.status !== false && dto.is_active !== false;
}

function dtoControlActive(dto: WorkflowBuilderControlDto): boolean {
  return dto.status !== false && dto.is_active !== false;
}

function resourceCategoryOf(row: WorkflowBuilderControlDto): string | undefined {
  return row.resource_category ?? row.resourceCategory;
}

function isManualDataModelRow(row: WorkflowBuilderControlDto): boolean {
  return row.category === 'data_models_mcps' && resourceCategoryOf(row) === 'Manual';
}

function safeControlKey(row: WorkflowBuilderControlDto): string {
  const raw = (row.uuid || row.unique_id || '').replace(/[^a-zA-Z0-9_-]/g, '');
  if (raw) return raw;
  let h = 0;
  const s = row.label || 'manual';
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return `k${Math.abs(h).toString(36)}`;
}

export type WorkflowSidebarPaletteState = {
  source: 'api' | 'fallback';
  collapsibleCategoryOrder: SidebarCategoryId[];
  categoryLabels: Record<SidebarCategoryId, string>;
  categoryIcons: Record<SidebarCategoryId, LucideIcon>;
  paletteItems: SidebarPaletteItem[];
};

function staticFallbackState(): WorkflowSidebarPaletteState {
  return {
    source: 'fallback',
    collapsibleCategoryOrder: [...SIDEBAR_PALETTE_SECTION_ORDER],
    categoryLabels: { ...SIDEBAR_CATEGORY_LABELS },
    categoryIcons: { ...SIDEBAR_CATEGORY_ICONS },
    paletteItems: [...SIDEBAR_PALETTE_ITEMS],
  };
}

function mapApiToPaletteState(data: WorkflowBuilderControlsResponse): WorkflowSidebarPaletteState | null {
  const activeCats = data.categories.filter(dtoCategoryActive).filter((c) => isSidebarCategoryId(c.category));
  if (activeCats.length === 0) return null;

  activeCats.sort(compareWorkflowBuilderCategoryRows);

  const categoryLabels = {} as Record<SidebarCategoryId, string>;
  const categoryIcons = {} as Record<SidebarCategoryId, LucideIcon>;
  const categoryUuidByKey = new Map<string, string>();
  for (const c of activeCats) {
    const id = c.category as SidebarCategoryId;
    categoryLabels[id] = c.label;
    categoryIcons[id] = lucideIconFromRef(c.icon);
    const catUuid = c.uuid?.trim();
    if (catUuid) categoryUuidByKey.set(c.category, catUuid);
  }

  const collapsibleCategoryOrder = activeCats
    .map((c) => c.category as SidebarCategoryId)
    .filter((id) => id !== SIDEBAR_CATEGORIES.TERMINAL);

  const paletteItems: SidebarPaletteItem[] = [];
  const activeControls = data.controls.filter(dtoControlActive).filter((c) => isSidebarCategoryId(c.category));

  for (const row of activeControls) {
    const category = row.category as SidebarCategoryId;
    /** Terminal / End is a fixed sidebar control — not driven by API rows. */
    if (category === SIDEBAR_CATEGORIES.TERMINAL) continue;
    const categoryUuid = categoryUuidByKey.get(row.category);
    const controlUuid = row.uuid?.trim() || undefined;
    const manual = isManualDataModelRow(row);
    const key = safeControlKey(row);

    const paletteType = manual
      ? row.palette_type?.trim() || `MCP_Manual_${key}`
      : row.palette_type?.trim();

    if (!manual && !paletteType) continue;

    const uniqueId = row.unique_id?.trim() || (manual ? `mcp-manual-${key}` : '');
    if (!uniqueId) continue;

    let Icon = lucideIconFromRef(manual ? 'lucide:Database' : row.icon);
    let canvasIcon = manual ? '🗄️' : row.icon_canvas?.trim() || '●';
    if (category === SIDEBAR_CATEGORIES.AGENTS) {
      const picked = pickAgentPalettePresentation(uniqueId);
      Icon = picked.Icon;
      canvasIcon = picked.canvasIcon;
    }

    const kindNorm = String(row.kind || 'node').toLowerCase();
    const itemKind: 'node' | 'action' = kindNorm === 'action' ? 'action' : 'node';

    if (itemKind === 'action') {
      paletteItems.push({
        kind: 'action',
        id: uniqueId,
        category,
        categoryUuid,
        actionId: (row.action_id ?? row.actionId)?.trim() || uniqueId,
        label: row.label,
        Icon,
        controlUuid,
      });
      continue;
    }

    paletteItems.push({
      kind: 'node',
      id: uniqueId,
      category,
      categoryUuid,
      type: paletteType!,
      label: row.label,
      canvasIcon,
      Icon,
      resourceCategory: resourceCategoryOf(row),
      controlUuid,
    });
  }

  return {
    source: 'api',
    collapsibleCategoryOrder,
    categoryLabels,
    categoryIcons,
    paletteItems,
  };
}

export async function fetchWorkflowBuilderControls(): Promise<WorkflowSidebarPaletteState> {
  try {
    const data = (await apiService.get(
      WORKFLOW_BUILDER_CONTROLS_PATH,
      {},
      'backend'
    )) as WorkflowBuilderControlsResponse;

    if (!data?.categories?.length || !data?.controls?.length) {
      return staticFallbackState();
    }

    const mapped = mapApiToPaletteState(data);
    return mapped ?? staticFallbackState();
  } catch {
    return staticFallbackState();
  }
}
